import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import SocialButton from '@/components/common/SocialButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '../ui/InputLabel';
import { CircleCheck, Circle } from 'lucide-react';
import Loader from '../ui/Loader';
import { useSubmitLock } from '@/hooks/useSubmitLock';

type FormValues = {
  email: string;
  password: string;
  confirm: string;
  remember?: boolean;
};

export default function RegisterForm({
  onSubmit,
  onGoogle,
  loading,
  formError,
}: Readonly<{
  onSubmit?: (data: {
    email: string;
    password: string;
    remember?: boolean;
  }) => Promise<void> | void;
  onGoogle?: () => Promise<void> | void;
  loading?: boolean;
  formError?: string | null;
}>) {
  const { t } = useTranslation(['register', 'auth', 'common']);

  const schema = useMemo(
    () =>
      z
        .object({
          email: z.email(t('register:errors.invalidEmail')),
          password: z
            .string()
            .min(12)
            .refine((val) => /[a-z]/.test(val))
            .refine((val) => /[A-Z]/.test(val))
            .refine((val) => /[0-9]/.test(val))
            .refine((val) => /[^a-zA-Z0-9]/.test(val)),
          confirm: z.string(),
          remember: z.boolean().optional(),
        })
        .refine((v) => v.password === v.confirm, {
          path: ['confirm'],
          message: t('register:errors.passwordMismatch'),
        }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', confirm: '', remember: false },
    mode: 'onChange',
  });

  const isValid = form.formState.isValid;
  const isSubmitting = form.formState.isSubmitting;
  const { isLocked, runExclusive } = useSubmitLock();

  const submitRegistration = async (data: FormValues) => {
    if (!onSubmit) return;
    await onSubmit({ email: data.email, password: data.password, remember: data.remember });
  };

  const passwordValue = form.watch('password');
  const meetsLength = passwordValue.length >= 12;
  const hasLowercase = /[a-z]/.test(passwordValue);
  const hasUppercase = /[A-Z]/.test(passwordValue);
  const hasDigit = /\d/.test(passwordValue);
  const hasSymbol = /[^A-Za-z0-9]/.test(passwordValue);

  return (
    <form onSubmit={form.handleSubmit((data) => runExclusive(() => submitRegistration(data)))}>
      <div className="space-y-4">
        <div className="space-y-1">
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:email')}
                id="email"
                type="email"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.email && (
            <p className="text-xs text-error-400">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('auth:password')}
                id="password"
                type="password"
                className="border-input-inverse bg-transparent"
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  if (form.getValues('confirm')) {
                    void form.trigger('confirm');
                  }
                }}
              />
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
            {[
              { met: meetsLength, label: t('auth:passwordRequirements.length') },
              { met: hasLowercase, label: t('auth:passwordRequirements.lowercase') },
              { met: hasUppercase, label: t('auth:passwordRequirements.uppercase') },
              { met: hasDigit, label: t('auth:passwordRequirements.number') },
              { met: hasSymbol, label: t('auth:passwordRequirements.symbol') },
            ].map((req) => (
              <div
                key={req.label}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-200 ${
                  req.met ? 'bg-success/15 text-success' : 'bg-white/10 text-tertiary-inverse'
                }`}
              >
                {req.met ? (
                  <CircleCheck className="size-3.5 shrink-0" />
                ) : (
                  <Circle className="size-3.5 shrink-0" />
                )}
                <span className="truncate">{req.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <Controller
            name="confirm"
            control={form.control}
            render={({ field }) => (
              <InputLabel
                label={t('register:confirm')}
                id="confirm"
                type="password"
                className="border-input-inverse bg-transparent  "
                classNameLabel="text-tertiary-inverse"
                classNameInput="text-white"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.confirm && (
            <p className="text-xs text-error-400">{form.formState.errors.confirm.message}</p>
          )}
        </div>

        {formError && (
          <p className="text-xs text-error-400">
            {/* si le thunk renvoie une clé i18n */}
            {formError.startsWith('errors.') ? t(formError) : formError}
          </p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full h-11 mt-6 rounded-full text-gray-800 bg-accent-500 hover:bg-accent-600 disabled:bg-button-primary-disable-inverse disabled:text-disable"
        disabled={!isValid || loading || isSubmitting || isLocked}
      >
        {(loading || isSubmitting || isLocked) && <Loader />}
        {t('register:submit')}
      </Button>

      <div className="my-16 space-y-4">
        <div className="flex items-center gap-3">
          <Separator className="flex-1 h-px bg-card-inverse" />
          <span className="text-sm text-secondary-inverse whitespace-nowrap">
            {t('register:orWith')}
          </span>
          <Separator className="flex-1 h-px bg-card-inverse" />
        </div>

        <SocialButton provider="google" onClick={onGoogle} disabled={loading} />
      </div>

      <p className="text-center text-sm font-medium text-white mt-4">
        {t('register:already')}{' '}
        <Link className="text-button-secondary hover:text-button-secondary/80" to="/login">
          {t('register:loginLink')}
        </Link>
      </p>

      <p className=" text-xs text-disable mt-8">
        {t('auth:recaptcha')}{' '}
        <Link
          className="underline text-disable "
          to="https://www.welya.io/politique-de-confidentialité"
          target="_blank"
        >
          {t('auth:confidentiality')}
        </Link>{' '}
        {t('auth:apply')}{' '}
        <Link
          className="underline text-disable"
          to="https://policies.google.com/terms"
          target="_blank"
        >
          {t('auth:terms')}
        </Link>
      </p>
    </form>
  );
}
